import type { FormaRentConfig } from '../types';

const VALID_THEMES = ['light', 'dark', 'auto'] as const;
const VALID_LANGS = ['ru', 'en'] as const;

// Whitelist разрешённых baseUrl. Атрибут data-forma-base-url от хост-сайта больше
// НЕ читается (см. security-review H1: подмена origin на evil.com).
// Для init({baseUrl}) — тестовые/dev URL проходят через этот же allow-list.
const ALLOWED_BASE_URLS: ReadonlyArray<string> = [
  __FORMA_ORIGIN__,
  'https://forma.rent',
  'https://forma.chebe.digital',
  'http://localhost:3000',
  'http://localhost:3001',
];

type Theme = (typeof VALID_THEMES)[number];
type Lang = (typeof VALID_LANGS)[number];

function isAllowedBaseUrl(url: string): boolean {
  const normalized = url.replace(/\/$/, '');
  return ALLOWED_BASE_URLS.some((allowed) => allowed.replace(/\/$/, '') === normalized);
}

/**
 * Ищет <script src="...embed.v2.js"> в DOM и читает data-forma-* атрибуты.
 * Если таких скриптов несколько — берёт последний (срабатывает двойной инклюд).
 * Невалидные theme/lang игнорируются.
 * Если нет ни data-forma-user, ни data-forma-owner — возвращает null:
 * без адресата витрины показывать нечего.
 */
export function readConfigFromScript(): Partial<FormaRentConfig> | null {
  const scripts = document.querySelectorAll<HTMLScriptElement>('script[src]');
  let target: HTMLScriptElement | null = null;

  for (const s of Array.from(scripts)) {
    // Смотрим на имя файла — базовый URL может быть любой.
    if (/embed\.v2\.js(\?|$|#)/.test(s.src)) {
      target = s;
    }
  }

  if (!target) return null;

  const user = target.getAttribute('data-forma-user');
  const owner = target.getAttribute('data-forma-owner');
  if (!user && !owner) return null;

  const result: Partial<FormaRentConfig> = {};
  if (user) result.user = user;
  if (owner) result.owner = owner;

  const theme = target.getAttribute('data-forma-theme');
  if (theme && (VALID_THEMES as ReadonlyArray<string>).includes(theme)) {
    result.theme = theme as Theme;
  }

  const lang = target.getAttribute('data-forma-lang');
  if (lang && (VALID_LANGS as ReadonlyArray<string>).includes(lang)) {
    result.lang = lang as Lang;
  }

  // data-forma-base-url НЕ читается — уязвимость подмены origin
  // (см. security-review H1). baseUrl задаётся только через init() разработчиком.

  return result;
}

/**
 * Мёрж двух источников конфига с дефолтами. fromInit перекрывает fromScript.
 * Бросает Error если ни user, ни owner не заданы нигде.
 */
export function mergeConfig(
  fromScript: Partial<FormaRentConfig> | null,
  fromInit: Partial<FormaRentConfig>,
): FormaRentConfig {
  const script = fromScript ?? {};
  const merged: Partial<FormaRentConfig> = { ...script, ...fromInit };

  if (!merged.user && !merged.owner) {
    throw new Error('[FormaRent] user or owner is required');
  }

  // baseUrl проходит через allow-list: если разработчик передал непонятный URL,
  // откатываемся на дефолт __FORMA_ORIGIN__ (собранный в билде).
  const rawBaseUrl = merged.baseUrl ?? __FORMA_ORIGIN__;
  const baseUrl = isAllowedBaseUrl(rawBaseUrl) ? rawBaseUrl : __FORMA_ORIGIN__;

  return {
    user: merged.user,
    owner: merged.owner,
    theme: merged.theme ?? 'auto',
    lang: merged.lang ?? 'ru',
    baseUrl,
  };
}
